import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";
import { useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { clickCta, viewItem } from "@/lib/analytics";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 12;
import { projectsQueryOptions, projectsByCategoryQueryOptions } from "@/lib/portfolio";
import type { Project } from "@/lib/portfolio";
import { excludeThreeD } from "@/lib/portfolio/exclude";
import { ProjectCover } from "./ProjectCover";


interface SelectedWorkProps {
  category?: string;
  eyebrow?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  sectionId?: string;
  className?: string;
  showHeader?: boolean;
  hideTier?: boolean;
}

export function SelectedWork({
  category = "websites",
  eyebrow = "OUR WORK",
  title,
  subtitle = "Every site we build is designed to be your most powerful sales tool - not just a pretty face.",
  sectionId = "work",
  className,
  showHeader = true,
  hideTier = false,
}: SelectedWorkProps = {}) {

  const options =
    category === "websites" ? projectsQueryOptions : projectsByCategoryQueryOptions(category);
  const { data: allProjects } = useSuspenseQuery(options);
  const projects = excludeThreeD(allProjects);
  const [lightbox, setLightbox] = useState<Project | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const queryClient = useQueryClient();
  const isEmpty = projects.length === 0;
  const visibleProjects = projects.slice(0, visibleCount);
  const hasMore = visibleCount < projects.length;

  useEffect(() => {
    if (!isEmpty) return;
    queryClient.invalidateQueries({ queryKey: options.queryKey });
  }, [isEmpty, queryClient, options.queryKey]);

  const heading = title ?? (
    <>
      <span className="block sm:inline">Websites that</span>{" "}
      <span className="block bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent sm:inline">
        deliver results.
      </span>
    </>
  );

  return (
    <section id={sectionId} className={cn("bg-background pt-6 pb-6 sm:pt-8 sm:pb-8", className)}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 xl:px-14">
        {showHeader && (
          <div className="mx-auto mb-10 max-w-3xl text-center">
            <span className="inline-block rounded-full bg-brand-blue/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-brand-blue">
              {eyebrow}
            </span>
            <h2 className="mt-4 font-display text-4xl font-bold tracking-tight text-gray-950 sm:text-5xl lg:text-5xl">
              {heading}
            </h2>
            <p className="mt-4 text-base text-gray-600 sm:text-lg">
              {subtitle}
            </p>
          </div>
        )}


        {isEmpty ? (
          <div
            aria-busy="true"
            aria-label="Loading projects"
            className="grid grid-cols-2 gap-5 sm:grid-cols-2 sm:gap-7 lg:grid-cols-3 lg:gap-8 xl:grid-cols-4 xl:gap-10"
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-2 sm:gap-7 lg:grid-cols-3 lg:gap-8 xl:grid-cols-4 xl:gap-10">
            {visibleProjects.map((p) => (
              <ProjectCard key={p.slug} project={p} onLightbox={() => setLightbox(p)} hideTier={hideTier} />
            ))}
          </div>
        )}

        {(hasMore || visibleCount > PAGE_SIZE) && (
          <div className="mt-8 flex justify-center sm:mt-10">
            <button
              type="button"
              onClick={() => {
                if (hasMore) {
                  clickCta("view_more_work", sectionId);
                  setVisibleCount((c) => Math.min(c + PAGE_SIZE, projects.length));
                } else {
                  clickCta("show_less_work", sectionId);
                  setVisibleCount(PAGE_SIZE);
                  document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }}
              className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-gray-950 shadow-sm ring-1 ring-black/10 transition hover:-translate-y-0.5 hover:shadow-md sm:text-base"
            >
              {hasMore ? "View more work" : "Show less"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

      </div>

      <Lightbox project={lightbox} onOpenChange={(open) => !open && setLightbox(null)} />
    </section>
  );
}


function SkeletonCard() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-surface-soft shadow-card">
      <div className="aspect-[4/3] w-full animate-pulse bg-muted" />
      <div className="p-4">
        <div className="h-2.5 w-1/3 animate-pulse rounded bg-ink/10" />
        <div className="mt-2 h-4 w-3/4 animate-pulse rounded bg-ink/15" />
      </div>
    </div>
  );
}


export function ProjectCard({ project, onLightbox, still = false, hideTier = false }: { project: Project; onLightbox: () => void; still?: boolean; hideTier?: boolean }) {
  const categoryText = project.categoryLabels?.join(" · ") ?? project.categoryLabel;
  const inner = (
    <div className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-surface-soft shadow-card transition-shadow hover:shadow-lg">
      <div className="relative aspect-[4/3] w-full shrink-0 overflow-hidden bg-muted">
        <div className="absolute inset-0 transition-transform duration-500 ease-out group-hover:scale-105">
          <ProjectCover project={project} still={still} />
        </div>
      </div>
      <div className="flex flex-1 flex-col justify-end p-2 sm:p-3 lg:p-4">
        <p className="truncate font-sans text-[9px] font-semibold uppercase tracking-[0.22em] text-ink-muted sm:text-[10px]">
          {categoryText}
        </p>
        <h3 className="mt-0.5 truncate font-display text-sm font-semibold tracking-tight text-ink sm:text-base">
          {project.title}
        </h3>
      </div>
    </div>
  );


  if (project.mode === "page") {
    return (
      <Link
        to="/work/$slug"
        params={{ slug: project.slug }}
        className="block h-full cursor-pointer"
        onClick={() => clickCta(project.title, "selected_work")}
      >
        {inner}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        viewItem(project.title, "portfolio_lightbox");
        onLightbox();
      }}
      className="block h-full w-full cursor-pointer text-left"
    >
      {inner}
    </button>
  );
}

export function Lightbox({
  project,
  onOpenChange,
}: {
  project: Project | null;
  onOpenChange: (o: boolean) => void;
}) {
  const open = !!project;
  const media = project?.media ?? [];
  const [idx, setIdx] = useState(0);
  const [imgSize, setImgSize] = useState<{ width: number; height: number } | null>(null);
  const [videoSize, setVideoSize] = useState<{ width: number; height: number } | null>(null);
  const [playerControlsDismissed, setPlayerControlsDismissed] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    setIdx(0);
  }, [project?.slug]);

  useEffect(() => {
    setPlayerControlsDismissed(false);
  }, [idx, project?.slug]);


  useEffect(() => {
    if (!open || media.length <= 1) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % media.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + media.length) % media.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, media.length]);

  // Measured viewport so the video can be sized in px to fit exactly — CSS
  // max-width/max-height on a <video> lets it overflow and get clipped.
  const [viewport, setViewport] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    if (!open) return;
    const update = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [open]);

  const fittedVideo = (() => {
    if (!videoSize || !viewport) return null;
    const gutter = viewport.w < 640 ? 24 : 32;
    const maxW = viewport.w - gutter;
    const maxH = viewport.h - (viewport.w < 640 ? 72 : 112);
    const scale = Math.min(maxW / videoSize.width, maxH / videoSize.height, 1);
    return {
      width: Math.floor(videoSize.width * scale),
      height: Math.floor(videoSize.height * scale),
    };
  })();

  const current = media[idx];
  const isMobileVideo =
    !!viewport && viewport.w < 640 && current?.type === "video";


  useEffect(() => {
    setImgSize(null);
    setVideoSize(null);
    const img = imgRef.current;
    if (img && img.complete) {
      setImgSize({ width: img.naturalWidth, height: img.naturalHeight });
    }
  }, [current?.src, project?.slug]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={contentRef}
        animate={false}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          requestAnimationFrame(() => contentRef.current?.focus({ preventScroll: true }));
        }}
        className="h-auto max-h-[calc(100svh-24px)] w-full max-w-[calc(100vw-24px)] gap-0 overflow-visible border-none bg-transparent p-0 shadow-none outline-none [&>button:last-child]:hidden sm:max-h-[calc(100svh-32px)] sm:w-auto sm:max-w-[calc(100vw-32px)]"
      >
        <DialogTitle className="sr-only">{project?.title ?? "Project preview"}</DialogTitle>
        {project && (
          <div
            className="mx-auto grid w-fit min-w-0 grid-rows-[auto] overflow-hidden rounded-2xl sm:max-h-[calc(100svh-32px)] sm:max-w-[calc(100vw-32px)]"
            style={
              current?.type === "video" && current.provider !== "youtube" && current.provider !== "vimeo" && fittedVideo
                ? { width: `${fittedVideo.width}px` }
                : undefined
            }
          >
            <div
               onPointerUpCapture={(event) => {
                 if (!isMobileVideo) return;
                 const target = event.target;
                 if (target instanceof Element && target.closest("button")) return;
                 setPlayerControlsDismissed((dismissed) => !dismissed);
               }}
              className="relative flex min-h-0 min-w-0 items-center justify-center overflow-hidden bg-black/5 leading-none"
              style={
                current?.type === "video" && current.provider !== "youtube" && current.provider !== "vimeo" && fittedVideo
                  ? { height: `${fittedVideo.height}px` }
                  : undefined
              }
            >
              {isMobileVideo &&
                (current?.provider === "youtube" || current?.provider === "vimeo") && (
                <div
                  className="absolute inset-0 z-10"
                  aria-hidden="true"
                />
                )}

              {current?.type === "video" ? (
                current.provider === "youtube" || current.provider === "vimeo" ? (
                  <div className="flex min-h-0 min-w-0 items-center justify-center">
                    <div
                      className={cn(
                        current.vertical
                          ? "aspect-[9/16] max-w-[calc(100vw-24px)] sm:max-w-[calc(100vw-32px)]"
                          : "aspect-video w-[calc(100vw-24px)] sm:w-[min(calc(100vw-32px),64rem)]",
                        "h-[calc(100svh-120px)] max-h-[calc(100svh-120px)] sm:h-[calc(100svh-112px)] sm:max-h-[calc(100svh-112px)]"
                      )}
                    >

                      <iframe
                        src={current.src}
                        title={project.title}
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        className="h-full w-full"
                      />
                    </div>
                  </div>
                ) : (
                  <video
                    key={current.src}
                    src={current.src}
                    poster={current.poster}
                    controls
                    autoPlay
                    loop
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={(e) => {
                      const video = e.currentTarget;
                      if (video.videoWidth > 0 && video.videoHeight > 0) {
                        setVideoSize({ width: video.videoWidth, height: video.videoHeight });
                      }
                    }}
                    style={
                      fittedVideo
                        ? { width: `${fittedVideo.width}px`, height: `${fittedVideo.height}px` }
                        : { width: "min(calc(100vw - 32px), 64rem)", height: "auto" }
                    }
                    className="block object-contain"
                  />

                )
              ) : (
                <img
                  ref={imgRef}
                  src={current?.src ?? project.cover}
                  alt={project.title}
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    setImgSize({ width: img.naturalWidth, height: img.naturalHeight });
                  }}
                  style={
                    imgSize
                      ? ({
                          "--native-width": `${imgSize.width}px`,
                          "--native-height": `${imgSize.height}px`,
                        } as CSSProperties)
                      : undefined
                  }
                  className="block h-auto w-auto object-contain max-h-[min(calc(100svh-120px),var(--native-height,100svh))] max-w-[min(calc(100vw-24px),var(--native-width,100vw))] sm:max-h-[min(calc(100svh-112px),var(--native-height,100svh))] sm:max-w-[min(calc(100vw-32px),var(--native-width,100vw))]"
                />

              )}

              {media.length > 1 && (
                <>
                  <button
                    type="button"
                    aria-label="Previous"
                    onClick={() => setIdx((i) => (i - 1 + media.length) % media.length)}
                    className={cn(
                      "absolute left-3 top-1/2 z-30 grid h-10 w-10 -translate-y-1/2 cursor-pointer place-items-center rounded-full bg-white/90 text-black shadow backdrop-blur transition-all duration-150 hover:scale-105",
                      playerControlsDismissed && "pointer-events-none opacity-0"
                    )}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    aria-label="Next"
                    onClick={() => setIdx((i) => (i + 1) % media.length)}
                    className={cn(
                      "absolute right-3 top-1/2 z-30 grid h-10 w-10 -translate-y-1/2 cursor-pointer place-items-center rounded-full bg-white/90 text-black shadow backdrop-blur transition-all duration-150 hover:scale-105",
                      playerControlsDismissed && "pointer-events-none opacity-0"
                    )}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>

          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
